import {
    addDateEventListener,
    checkIfValueIsStep,
    debouncedFetchDataAndUpdateFields,
    fetchDataAndUpdateFields,
    formatDate,
    formatToCurrency,
    ifValueIsMin,
    initLoanBtnListener,
    initSteps,
    mapSteps,
    updateRangeBarColor,
} from './calculatorsLogic';

/**
 * Calculator script
 */
const calcInit = (calcType, siteUrl, apiDomain, context, defaultValueBig, calcAmountSteps, calcSettings, endpoint) => {
    // Extract necessary elements and configurations
    const {
        id,
        inputAmount,
        rangeAmount,
        fromAmountDisplay,
        toAmountDisplay,
        inputPeriod,
        rangePeriod,
        periods,
        installmentFrom,
        rrsoDiv,
        datepicker,
        datepickerMobile,
        loanButton,
        formLinks,
        promoBar
    } = context.calculatorFields;

    const {config} = context;
    const {params, offers, steps: defaultSteps} = config;
    const {formLink, formLinkMobile} = formLinks;

    const rangeBarColors = {
        leftSide: 'black',
        rightSide: 'white',
    };

    let amountMin;
    let amountMax;
    let stepsAmount = [];
    let stepsPeriod = [];
    // eslint-disable-next-line no-unused-vars
    let previousInputAmountValue = '';
    // eslint-disable-next-line no-unused-vars
    let previousInputPeriodValue = '';

    // Initialize current state
    const currentState = {
        currentAmountValue: 0,
        currentPeriodValue: 0,
        currentDate: new Date(),
        currentOffer: {},
        currentBtnLoanLink: '',
        periodValues: [],
        isPromoCalc: checkURLForPromo(endpoint)
    };

    initializeState(defaultValueBig, calcAmountSteps, defaultSteps, params, calcSettings);
    fetchAndUpdateInitialData(apiDomain, siteUrl, context);
    setupEventListeners();
    initCollapsibleButtonTextToggle();
    initTabAndCollapseHandler();
    preventScrollOnDatePicker(id);
    updateOffer();
    initRangeButtons(periods, rangePeriod, currentState.currentOffer, offers);

    // Function definitions

    function initializeState(defaultValueBig, calcAmountSteps, defaultSteps, params, calcSettings) {
        let {
            fromAmount,
            toAmount,
        } = params;

        const {
            defaultAmount,
            defaultAmountLP,
            defaultPeriod,
            defaultPeriodLP,
            rrso,
            minFirstRepaymentDate,
            maxFirstRepaymentDate,
            defaultFirstRepaymentDate,
        } = params;

        fromAmount = getDefaultOrHighest(offers, fromAmount, toAmount).fromAmount;
        toAmount = getDefaultOrHighest(offers, fromAmount, toAmount).toAmount;

        // Initialize amount steps
        stepsAmount = calcAmountSteps
            ? calcAmountSteps.map(item => ({
                fromAmount: parseInt(item.calc_step_fromAmount, 10),
                toAmount: parseInt(item.calc_step_toAmount, 10),
                step: parseInt(item.calc_step, 10),
            }))
            : defaultSteps;

        // Set up amount values
        const initialAmount = defaultValueBig ? defaultAmountLP : defaultAmount;
        currentState.currentAmountValue = initialAmount;
        inputAmount.value = formatToCurrency(initialAmount);

        // Set up amount range
        amountMin = fromAmount;
        amountMax = toAmount;
        rangeAmount.min = fromAmount;
        rangeAmount.max = toAmount;
        rangeAmount.value = initialAmount;
        stepsAmount = mapSteps(stepsAmount, inputAmount);

        // Update amount displays
        fromAmountDisplay.textContent = fromAmount.toLocaleString('fr-FR');
        toAmountDisplay.textContent = toAmount.toLocaleString('fr-FR');

        // Initialize steps and progress bar for amount
        initSteps(stepsAmount, rangeAmount, initialAmount);
        updateRangeBarColor(initialAmount, rangeAmount, amountMin, amountMax, rangeBarColors);

        // Set up period values
        const initialPeriod = defaultValueBig ? defaultPeriodLP : defaultPeriod;
        currentState.currentPeriodValue = initialPeriod;
        inputPeriod.value = `${initialPeriod} mies.`;

        if (inputAmount) {
            // Extract the id from the promoBar element
            const elementId = inputAmount.id; // e.g., 'promo-bar-0'

            // Split the id to get the number at the end
            const idParts = elementId.split('');
            const calcId = idParts[idParts.length - 1]; // This should be the number

            // Assuming you have access to the necessary variables
            updatePromoBar(
                calcSettings.calc_settings_repeater[calcId].calc_promo,
                initialAmount,
                promoBar,
                calcId,
                offers,
                currentState,
                params.defaultOffer
            );
        }

        // Initialize datepickers
        datepicker.min = formatDate(minFirstRepaymentDate);
        datepicker.max = formatDate(maxFirstRepaymentDate);
        datepickerMobile.min = formatDate(minFirstRepaymentDate);
        datepickerMobile.max = formatDate(maxFirstRepaymentDate);
        currentState.currentDate = formatDate(defaultFirstRepaymentDate);

        // Set up RRSO display
        const rrsoValue = parseFloat(rrso);
        if (!isNaN(rrsoValue)) {
            rrsoDiv.textContent = `${rrsoValue.toFixed(2).replace('.', ',')}%`;
        } else {
            console.error('Invalid RRSO value:', rrso);
        }

        // Initialize installment display
        if (installmentFrom) {
            installmentFrom.textContent = formatToCurrency(0);
        }
    }

    function fetchAndUpdateInitialData(apiDomain, siteUrl, context) {
        fetchDataAndUpdateFields(
            apiDomain,
            currentState.currentAmountValue,
            currentState.currentPeriodValue,
            currentState.currentDate,
            context,
            currentState.isPromoCalc
        )
            .then(buttonLinks => {
                currentState.currentBtnLoanLink = `${siteUrl}${buttonLinks.currentBtnLoanLink}`;
                formLink.href = formLinkMobile.href = `${apiDomain}${buttonLinks.formLink}`;
            })
            .catch(error => {
                console.error('Error fetching data and updating fields:', error);
            });

        initLoanBtnListener(loanButton, currentState);
    }

    function setupEventListeners() {
        // Amount field listeners
        addRangeEventListeners(inputAmount, rangeAmount);
        addInputEventListeners(inputAmount);

        // Period field listeners
        addRangeEventListeners(inputPeriod, rangePeriod);
        addInputEventListeners(inputPeriod);

        // Datepicker listeners
        addDateEventListener(calcType, datepicker, context, currentState.isPromoCalc, currentState, siteUrl, formLinks);
        addDateEventListener(calcType, datepickerMobile, context, currentState.isPromoCalc, currentState, siteUrl, formLinks);
    }

    function addRangeEventListeners(input, range) {
        range.addEventListener('input', () => {
            const rangeValue = parseInt(range.value, 10);

            if (input === inputAmount) {
                // Update current amount value before updating offer
                currentState.currentAmountValue = rangeValue < 1000 ? 1000 : rangeValue;
            } else if (input === inputPeriod) {
                // Update current period value before updating offer
                const periodValues = currentState.periodValues;
                const adjustedRangeValue = Math.min(Math.max(rangeValue, 0), periodValues.length - 1);
                currentState.currentPeriodValue = periodValues[adjustedRangeValue];

                updateActivePeriodLabel(periods, adjustedRangeValue);
            }

            // Update offer and state before using period values
            setRangeAndSteps(rangeValue, range, input);

            const periodValues = currentState.periodValues;
            const min = input === inputAmount ? amountMin : 0;
            const max = input === inputAmount ? amountMax : periodValues.length - 1;

            if (inputAmount) {
                // Extract the id from the promoBar element
                const elementId = inputAmount.id; // e.g., 'promo-bar-0'

                // Split the id to get the number at the end
                const idParts = elementId.split('');
                const calcId = idParts[idParts.length - 1]; // This should be the number

                // Assuming you have access to the necessary variables
                updatePromoBar(
                    calcSettings.calc_settings_repeater[calcId].calc_promo,
                    rangeAmount,
                    promoBar,
                    calcId,
                    offers,
                    currentState,
                    params.defaultOffer
                );
            }

            if (input === inputAmount) {
                input.value = formatToCurrency(currentState.currentAmountValue);
                updateRangeBarColor(rangeValue, range, min, max, rangeBarColors);
                ifValueIsMin(inputAmount, currentState.currentAmountValue, amountMin, rangeAmount);
                initSteps(stepsAmount, rangeAmount, rangeValue);
            }
            // Fetch data and update fields
            debouncedFetchDataAndUpdateFields(
                apiDomain,
                currentState.currentAmountValue,
                currentState.currentPeriodValue,
                currentState.currentDate,
                context,
                currentState.isPromoCalc
            )
                .then(buttonLinks => {
                    currentState.currentBtnLoanLink = `${siteUrl}${buttonLinks.currentBtnLoanLink}`;
                    formLink.href = formLinkMobile.href = `${apiDomain}${buttonLinks.formLink}`;
                })
                .catch(error => {
                    console.error('Error fetching data:', error);
                });
        });
    }

    function updateActivePeriodLabel(periodsElement, activeIndex) {
        const spans = periodsElement.querySelectorAll('span');
        spans.forEach((span, index) => {
            span.classList.toggle('active', index === activeIndex);
        });
    }

    function updatePeriodLabels(periodsElement, periodValues, activeIndex) {
        // Clear the content of periodsElement
        periodsElement.innerHTML = '';

        // Generate and insert span elements based on periodValues
        periodValues.forEach((period, index) => {
            const span = document.createElement('span');
            span.setAttribute('data-value', period);
            span.textContent = period;

            // Set active class based on the current period value
            if (index === activeIndex) {
                span.classList.add('active');
            }

            // Add click listener to update the active state
            span.addEventListener('click', () => {
                currentState.currentPeriodValue = period;

                // Update the rangePeriod value to match the selected period
                rangePeriod.value = index;

                // Update the active state for all spans
                updateActivePeriodLabel(periodsElement, index);

                // Update inputPeriod value
                inputPeriod.value = `${currentState.currentPeriodValue} mies.`;

                // Fetch data and update fields
                fetchDataAfterInputChange();
            });

            periodsElement.appendChild(span);
        });

        // Update the active period label
        updateActivePeriodLabel(periodsElement, activeIndex);
    }

    function setRangeAndSteps(rangeValue, range, input) {
        if (input === inputAmount) {
            if (
                rangeValue < currentState.currentOffer.fromAmount ||
                rangeValue > currentState.currentOffer.toAmount
            ) {
                updateOffer(input);

                // Adjust period if necessary
                adjustPeriodIfOutOfBounds();

                // Update period input and labels immediately
                updatePeriodInputAndLabels();
            } else {
                updateOffer(input);
            }

            initSteps(stepsAmount, range, rangeValue);
        } else {
            const periodValues = currentState.periodValues;
            const selectedPeriod = periodValues[rangeValue];

            if (
                selectedPeriod < currentState.currentOffer.fromPeriod ||
                selectedPeriod > currentState.currentOffer.toPeriod
            ) {
                updateOffer(input);

                if (Object.keys(currentState.currentOffer).length > 0) {
                    adjustAmountIfOutOfBounds();
                }

                initSteps(stepsPeriod, range, selectedPeriod);
            }
        }
    }

    function updateOffer(input) {
        const {
            defaultPeriod,
            defaultPeriodLP,
        } = params;
        // Check if it's the first load
        // eslint-disable-next-line no-prototype-builtins
        const isFirstLoad = !currentState.hasOwnProperty('isFirstLoad') || currentState.isFirstLoad;

        const exactOffer = findExactOffer();
        if (exactOffer) {
            currentState.currentOffer = exactOffer;
        } else {
            adjustOfferBasedOnInput(input);
        }

        // Update the period value
        if (isFirstLoad) {
            // Set initialPeriod on the first load
            currentState.currentPeriodValue = defaultValueBig ? defaultPeriodLP : defaultPeriod;
            currentState.isFirstLoad = false; // Mark as not the first load anymore
        } else {
            // Update to the default period of the current offer on subsequent updates
            currentState.currentPeriodValue = currentState.currentOffer.defaultPeriod;
        }

        // Update periodValues based on the new offer
        currentState.periodValues = currentState.currentOffer.periodsList.map((p) => p.period);

        // Update period input and labels immediately
        updatePeriodInputAndLabels();
    }


    function updatePeriodInputAndLabels() {
        // Update rangePeriod min, max, and value
        rangePeriod.min = 0;
        rangePeriod.max = currentState.periodValues.length - 1;

        // Find the index of the current period value
        const periodIndex = currentState.periodValues.indexOf(currentState.currentPeriodValue);
        rangePeriod.value = periodIndex !== -1 ? periodIndex : 0;

        // Update inputPeriod value
        inputPeriod.value = `${currentState.currentPeriodValue} mies.`;
        // Update the period labels in the UI
        updatePeriodLabels(periods, currentState.periodValues, periodIndex);

        // Assuming you have access to the necessary variables
        if (inputAmount) {
            // Extract the id from the promoBar element
            const elementId = inputAmount.id; // e.g., 'promo-bar-0'

            // Split the id to get the number at the end
            const idParts = elementId.split('');
            const calcId = idParts[idParts.length - 1]; // This should be the number

            // Assuming you have access to the necessary variables
            updatePromoBar(
                calcSettings.calc_settings_repeater[calcId].calc_promo,
                rangeAmount,
                promoBar,
                calcId,
                offers,
                currentState,
                params.defaultOffer
            );
        }
    }

    function findExactOffer() {
        const offer = offers
            .filter(offer =>
                currentState.currentAmountValue >= offer.fromAmount &&
                currentState.currentAmountValue <= offer.toAmount
            )
            .reduce((closest, current) => {
                const currentDiff = Math.abs(currentState.currentAmountValue - current.fromAmount);
                const closestDiff = Math.abs(currentState.currentAmountValue - closest.fromAmount);
                return currentDiff > closestDiff ? current : closest;
            });

        if (offer) {
            computePeriodBounds(offer);
        }

        return offer;
    }

    function adjustOfferBasedOnInput(input) {
        if (input === inputAmount) {
            // Adjust offer based on amount input change
            const amountOffers = offers.filter(
                offer =>
                    currentState.currentAmountValue >= offer.fromAmount &&
                    currentState.currentAmountValue <= offer.toAmount
            );

            if (amountOffers.length > 0) {
                currentState.currentOffer = amountOffers[0];
            } else {
                // Find the closest offer by amount
                currentState.currentOffer = findClosestOfferByAmount(currentState.currentAmountValue);
                // Adjust currentAmountValue to be within the offer's range
                currentState.currentAmountValue = currentState.currentOffer.fromAmount;
                inputAmount.value = formatToCurrency(currentState.currentAmountValue);
                rangeAmount.value = currentState.currentAmountValue;
            }

            currentState.currentPeriodValue = currentState.currentOffer.defaultPeriod;

            // Adjust period if necessary
            adjustPeriodIfOutOfBounds();
        } else if (input === inputPeriod) {
            // Adjust offer based on period input change
            const periodOffers = offers.filter(
                offer =>
                    currentState.currentPeriodValue >= offer.fromPeriod &&
                    currentState.currentPeriodValue <= offer.toPeriod
            );

            if (periodOffers.length > 0) {
                currentState.currentOffer = periodOffers[0];
            } else {
                // Find the closest offer by period
                currentState.currentOffer = findClosestOfferByPeriod(currentState.currentPeriodValue);
                // Adjust currentPeriodValue to be within the offer's range
                currentState.currentPeriodValue = currentState.currentOffer.fromPeriod;
                inputPeriod.value = `${currentState.currentPeriodValue} mies.`;
                const periodIndex = currentState.periodValues.indexOf(currentState.currentPeriodValue);
                rangePeriod.value = periodIndex;
            }

            // Adjust amount if necessary
            adjustAmountIfOutOfBounds();
        }

        // After adjusting, update the range buttons
        initRangeButtons(periods, rangePeriod, currentState.currentOffer, offers);
    }

    function findClosestOfferByAmount(amount) {
        return offers.reduce((prev, curr) => {
            const prevDiff = Math.abs(prev.fromAmount - amount);
            const currDiff = Math.abs(curr.fromAmount - amount);
            return currDiff < prevDiff ? curr : prev;
        });
    }

    function findClosestOfferByPeriod(period) {
        return offers.reduce((prev, curr) => {
            const prevDiff = Math.abs(prev.fromPeriod - period);
            const currDiff = Math.abs(curr.fromPeriod - period);
            return currDiff < prevDiff ? curr : prev;
        });
    }

    function computePeriodBounds(offer) {
        const periods = offer.periodsList.map(p => p.period);
        offer.fromPeriod = Math.min(...periods);
        offer.toPeriod = Math.max(...periods);
    }

    function adjustPeriodIfOutOfBounds() {
        const {FromPeriod, ToPeriod} = currentState.currentOffer;

        if (
            currentState.currentPeriodValue < FromPeriod ||
            currentState.currentPeriodValue > ToPeriod
        ) {
            currentState.currentPeriodValue = currentState.currentOffer.defaultPeriod;
            inputPeriod.value = `${currentState.currentPeriodValue} mies.`;
            const periodIndex = currentState.periodValues.indexOf(currentState.currentPeriodValue);
            rangePeriod.value = periodIndex !== -1 ? periodIndex : 0;
        }
    }

    function adjustAmountIfOutOfBounds() {
        if (
            currentState.currentAmountValue < currentState.currentOffer.fromAmount ||
            currentState.currentAmountValue > currentState.currentOffer.toAmount
        ) {
            currentState.currentAmountValue = currentState.currentOffer.toAmount;
            inputAmount.value = formatToCurrency(currentState.currentAmountValue);
            rangeAmount.value = currentState.currentAmountValue;
            updateRangeBarColor(currentState.currentAmountValue, rangeAmount, amountMin, amountMax, rangeBarColors);
        }
    }

    function debounce(func, delay) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    function addInputEventListeners(input) {
        let previousInputValue = '';

        // Allow only numeric characters
        input.addEventListener('input', event => {
            event.target.value = event.target.value.replace(/[^0-9]/g, '');
        });

        // Clear the input on focus
        input.addEventListener('focus', () => {
            previousInputValue = input.value;
            input.value = '';
        });

        // Validate and update values on blur
        input.addEventListener('blur', () => {
            let inputValue = parseInt(event.target.value.replace(/[^0-9]/g, ''), 10);

            // If user did not change value, restore previous value
            if (isNaN(inputValue)) {
                input.value = previousInputValue;
                inputValue = parseInt(previousInputValue, 10);
            }

            if (input === inputAmount) {
                handleAmountInputBlur(inputValue);
            } else if (input === inputPeriod) {
                handlePeriodInputBlur(inputValue);
            }
        });

        // Debounced function for handling input changes
        const debouncedHandleInput = debounce(event => {
            let inputValue = parseInt(event.target.value.replace(/[^0-9]/g, ''), 10);

            if (input === inputAmount) {
                handleAmountInputBlur(inputValue);
            } else if (input === inputPeriod) {
                handlePeriodInputBlur(inputValue);
            }
        }, 500);

        input.addEventListener('keydown', debouncedHandleInput);

        // Handle Enter key to trigger blur event
        input.addEventListener('keyup', event => {
            if (event.key === 'Enter') {
                input.blur();
            }
        });
    }

    function handleAmountInputBlur(inputValue) {
        if (inputValue !== currentState.currentAmountValue) {
            previousInputAmountValue = currentState.currentAmountValue;
            currentState.currentAmountValue = inputValue;

            // Validate and adjust input value within min and max bounds
            if (inputValue < amountMin) {
                currentState.currentAmountValue = amountMin;
            } else if (inputValue > amountMax) {
                currentState.currentAmountValue = amountMax;
            } else {
                // Adjust to the closest valid step
                currentState.currentAmountValue = checkIfValueIsStep(inputValue, stepsAmount);
            }

            // Update input and range values
            inputAmount.value = formatToCurrency(currentState.currentAmountValue);
            rangeAmount.value = currentState.currentAmountValue;

            // Update the offer and related values
            setRangeAndSteps(currentState.currentAmountValue, rangeAmount, inputAmount);

            updateRangeBarColor(currentState.currentAmountValue, rangeAmount, amountMin, amountMax, rangeBarColors);
            ifValueIsMin(inputAmount, currentState.currentAmountValue, amountMin, rangeAmount);

            // Fetch data and update fields
            fetchDataAfterInputChange();
        }
    }

    function handlePeriodInputBlur(inputValue) {
        if (inputValue !== currentState.currentPeriodValue) {
            previousInputPeriodValue = currentState.currentPeriodValue;
            currentState.currentPeriodValue = inputValue;

            // Validate and adjust input value within available period values
            const periodValues = currentState.periodValues;
            const minPeriod = Math.min(...periodValues);
            const maxPeriod = Math.max(...periodValues);

            if (inputValue < minPeriod) {
                currentState.currentPeriodValue = minPeriod;
            } else if (inputValue > maxPeriod) {
                currentState.currentPeriodValue = maxPeriod;
            } else {
                // Adjust to the closest valid period
                currentState.currentPeriodValue = getClosestPeriodValue(inputValue, periodValues);
            }

            // Update input and range values
            inputPeriod.value = `${currentState.currentPeriodValue} mies.`;
            const periodIndex = periodValues.indexOf(currentState.currentPeriodValue);
            rangePeriod.value = periodIndex;

            // Update the offer and related values
            setRangeAndSteps(periodIndex, rangePeriod, inputPeriod);

            // Update the active period label
            updateActivePeriodLabel(rangePeriod, periodIndex);

            // Fetch data and update fields
            fetchDataAfterInputChange();
        }
    }

    function fetchDataAfterInputChange() {
        debouncedFetchDataAndUpdateFields(
            apiDomain,
            currentState.currentAmountValue,
            currentState.currentPeriodValue,
            currentState.currentDate,
            context,
            currentState.isPromoCalc
        )
            .then(buttonLinks => {
                currentState.currentBtnLoanLink = `${siteUrl}${buttonLinks.currentBtnLoanLink}`;
                formLink.href = formLinkMobile.href = `${apiDomain}${buttonLinks.formLink}`;
            })
            .catch(error => {
                console.error('Error fetching data:', error);
            });
    }

    function getClosestPeriodValue(value, periodValues) {
        return periodValues.reduce((prev, curr) => {
            return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
        });
    }

    function initCollapsibleButtonTextToggle() {
        const buttons = document.querySelectorAll('.calculator .calc__button--modal');

        buttons.forEach(button => {
            const collapsedText = button.dataset?.collapsed || 'Rozwiń';
            const unCollapsedText = button.dataset?.uncollapsed || 'Zwiń';

            button.addEventListener('click', () => {
                button.textContent = button.classList.contains('collapsed') ? collapsedText : unCollapsedText;
            });
        });
    }

    function initTabAndCollapseHandler() {
        const buttons = document.querySelectorAll('.calc__button--modal');
        const buttonToTabMapping = [
            {buttonId: 'calculator-0-0-tab', tabIds: ['calculator-0-0-tab', 'calculator-1-0-tab']},
            {buttonId: 'calculator-0-1-tab', tabIds: ['calculator-0-1-tab', 'calculator-1-1-tab']},
            {buttonId: 'calculator-1-0-tab', tabIds: ['calculator-0-0-tab', 'calculator-1-0-tab']},
            {buttonId: 'calculator-1-1-tab', tabIds: ['calculator-0-1-tab', 'calculator-1-1-tab']},
        ];

        buttonToTabMapping.forEach(({buttonId, tabIds}) => {
            const button = document.getElementById(buttonId);
            if (button) {
                button.addEventListener('click', event => {
                    event.preventDefault();

                    buttons.forEach(btn => {
                        const collapseElement = document.querySelector(btn.dataset.bsTarget);
                        if (collapseElement) {
                            const bsCollapse = bootstrap.Collapse.getOrCreateInstance(collapseElement, {
                                toggle: false,
                            });
                            bsCollapse.hide();
                        }
                    });

                    tabIds.forEach(tabId => {
                        const tabElement = document.getElementById(tabId);
                        if (tabElement) {
                            const tabInstance = bootstrap.Tab.getOrCreateInstance(tabElement);
                            tabInstance.show();
                        }
                    });
                });
            }
        });
    }

    function initRangeButtons(periodsElement, rangePeriod, currentOffer, offersList) {
        let defaultOffer;

        if (typeof currentOffer === 'string') {
            defaultOffer = offersList.find(offerItem =>
                offerItem.periodsList.some(periodItem => periodItem.offerName === currentOffer)
            );
        } else if (typeof currentOffer === 'object') {
            defaultOffer = currentOffer;
        } else {
            throw new Error('currentOffer must be a string or an object');
        }

        // Extract periods into an array
        currentState.periodValues = defaultOffer.periodsList.map(periodItem => periodItem.period);

        // Set the new min and max values for rangePeriod
        rangePeriod.min = 0;
        rangePeriod.max = currentState.periodValues.length - 1;

        // Update rangePeriod.value to match the index of currentPeriodValue in periodValues
        const periodIndex = currentState.periodValues.indexOf(currentState.currentPeriodValue);
        rangePeriod.value = periodIndex !== -1 ? periodIndex : 0;

        // Clear the content of periodsElement
        periodsElement.innerHTML = '';

        // Generate and insert span elements based on currentState.periodValues
        currentState.periodValues.forEach((period, index) => {
            const span = document.createElement('span');
            span.setAttribute('data-value', period);
            span.textContent = period;

            // Set active class based on the current period value
            if (currentState.currentPeriodValue === period) {
                span.classList.add('active');
            }

            // Add click listener to update the active state
            span.addEventListener('click', () => {
                currentState.currentPeriodValue = period;

                // Update the rangePeriod value to match the selected period
                rangePeriod.value = index;

                // Update the active state for all spans
                document.querySelectorAll('.calc__range-labels span').forEach(s => {
                    s.classList.toggle('active', s === span);
                });

                // Fetch data and update fields
                debouncedFetchDataAndUpdateFields(
                    apiDomain,
                    currentState.currentAmountValue,
                    currentState.currentPeriodValue,
                    currentState.currentDate,
                    context,
                    currentState.isPromoCalc
                )
                    .then(buttonLinks => {
                        currentState.currentBtnLoanLink = `${siteUrl}${buttonLinks.currentBtnLoanLink}`;
                        formLink.href = formLinkMobile.href = `${apiDomain}${buttonLinks.formLink}`;
                    })
                    .catch(error => {
                        console.error('Error fetching data:', error);
                    });
            });

            periodsElement.appendChild(span);
        });
    }

    function findPeriodEntries(data, targetPeriod) {
        if (!data || !Array.isArray(data.periodsList)) {
            console.warn('Invalid or missing periodsList in data:', data);
            return {}; // Return an empty object if the data is not valid
        }

        // Use find() for simplicity
        const matchingEntry = data.periodsList.find(periodEntry => periodEntry.period === targetPeriod);

        return matchingEntry || {}; // Return the matching entry or an empty object
    }

    function updatePromoBar(calcPromo, amount, promoBar, calcId, offers, currentState, defaultOfferName) {
        let result = {}
        if (currentState.currentOffer && Object.keys(currentState.currentOffer).length > 0) {
            result = findPeriodEntries(currentState.currentOffer, currentState.currentPeriodValue);
        } else {
            const defaultOffer = offers.find(offerItem =>
                offerItem.periodsList.some(periodItem => periodItem.offerName === defaultOfferName)
            );
            result = findPeriodEntries(defaultOffer, currentState.currentPeriodValue);
        }

        // Check if calcPromo.is_promo is true
        if (calcPromo.is_promo) {
            amount = Number.isInteger(amount) ? amount : parseInt(amount.value, 10);
            const amountMin = parseInt(calcPromo.amout_min, 10);
            const amountMax = parseInt(calcPromo.amout_max, 10);

            if (amount >= amountMin && amount <= amountMax) {
                promoBar.style.display = 'block'; // Ensure it's visible
                requestAnimationFrame(() => {
                    promoBar.classList.add('active'); // Trigger the slide animation
                });
            } else {
                promoBar.classList.remove('active');
                promoBar.style.display = 'none';
            }
        }
        // Check if specific condition for offers and currentState matches
        else {
            // Find the calc__box element
            const calcBox = document.getElementById('calculator-relative' + calcId);

            // Try to find the existing promo bar div
            let promoBarDiv = calcBox.querySelector('.calc__promo-bar');

            if (result?.promoName && result?.promoUrl) {
                if (!promoBarDiv) {
                    // Create a new promo bar div if it doesn't exist
                    promoBarDiv = document.createElement('div');
                    promoBarDiv.classList.add('calc__promo-bar');

                    const promoBarLabel = document.createElement('div');
                    promoBarLabel.classList.add('mb-1');
                    promoBarLabel.textContent = result.promoName;

                    const promoBarLink = document.createElement('a');
                    promoBarLink.classList.add('small', 'text-white');
                    promoBarLink.href = result.promoUrl;
                    promoBarLink.textContent = 'Regulamin promocji';

                    // Append label and link to the promo bar div
                    promoBarDiv.appendChild(promoBarLabel);
                    promoBarDiv.appendChild(promoBarLink);

                    // Append the promo bar div to calcBox
                    calcBox.appendChild(promoBarDiv);
                } else {
                    // Update existing promo bar content
                    promoBarDiv.querySelector('.mb-1').textContent = result.promoName;
                    promoBarDiv.querySelector('a').href = result.promoUrl;
                }

                promoBarDiv.style.display = 'block'; // Ensure it's visible
                requestAnimationFrame(() => {
                    promoBarDiv.classList.add('active'); // Trigger the slide animation
                });
            } else {
                if (promoBarDiv) {
                    promoBarDiv.classList.remove('active');
                    promoBarDiv.style.display = 'none';
                }
            }
        }
    }

    function isFirefox() {
        return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    }

    // Function to prevent scrolling when the date picker is open
    function preventScrollOnDatePicker(id) {
        // Get the input element with the specified ID
        const datePicker = document.getElementById(`datepickerMobile${id}`);

        // If the element with the given ID is not found, log an error and exit the function
        if (!datePicker) {
            console.error(`Element with ID "datepickerMobile${id}" not found`);
            return;
        }

        // Function to open the date picker and disable scrolling in Firefox
        const openPicker = () => {
            // Open the date picker
            datePicker.showPicker();

            // If the browser is Firefox, disable scrolling on the body
            if (isFirefox()) {
                document.body.style.overflow = "hidden"; // Prevent scrolling in Firefox
            }
        };

        // Function to restore scrolling once the date picker is closed
        const restoreScroll = () => {
            // If the browser is Firefox, enable scrolling on the body again
            if (isFirefox()) {
                document.body.style.overflow = ""; // Restore scrolling in Firefox
            }
        };

        // Add an event listener to open the picker when the input is clicked
        datePicker.addEventListener("click", (event) => {
            event.preventDefault(); // Prevent default behavior
            openPicker();
        });

        // Add event listeners to restore scrolling when the input loses focus
        datePicker.addEventListener("blur", restoreScroll);
        datePicker.addEventListener("focusout", restoreScroll);
    }

    function getDefaultOrHighest(offers, fromAmount, toAmount) {
        // Find the offer where fromAmount matches
        const matchingOffer = offers.find(offer =>
            fromAmount >= offer.fromAmount && fromAmount <= offer.toAmount
        );

        // Find the highest toAmount in the offers
        const highestToAmount = Math.max(...offers.map(offer => offer.toAmount));

        // Default result
        let resultFromAmount = null;
        let resultToAmount = null;

        if (matchingOffer) {
            // If toAmount is within the range of the matched offer
            if (toAmount >= matchingOffer.fromAmount && toAmount <= matchingOffer.toAmount) {
                resultFromAmount = fromAmount;
                resultToAmount = toAmount;
            } else {
                // If toAmount exceeds the range, take the highest toAmount
                resultFromAmount = fromAmount;
                resultToAmount = highestToAmount;
            }
        } else {
            // If no matching fromAmount, take the highest range values
            resultFromAmount = Math.max(...offers.map(offer => offer.fromAmount));
            resultToAmount = highestToAmount;
        }

        // Return the result
        return {
            fromAmount: resultFromAmount,
            toAmount: resultToAmount
        };
    }

    function checkURLForPromo(url) {
        try {
            // Define regex patterns for configuration_promo and configuration
            const promoPattern = /configuration_promo(?=\/v2)/;
            const configPattern = /configuration(?=\/v2)/;
            // Check for configuration_promo
            if (promoPattern.test(url)) {
                return 1;
            }

            // Check for configuration
            if (configPattern.test(url)) {
                return 0;
            }

            console.log("Neither 'configuration_promo' nor 'configuration' found before 'v2' in the URL.");
            return 0;
        } catch (error) {
            // Handle any errors during the processing
            return 0;
        }
    }
};

export {calcInit};
